import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import { SegmentedBannerGrid } from '~/src/components/SegmentedBanner/SegmentedBannerGrid';
import Routers from '~/src/features/equipment/pageBlocks/Routers';
import { getOrderHandler } from '~/src/features/equipment/shop/helpers';
import type { TState } from '~/src/store';

import Characteristics from './components/Characteristics';
import Description from './components/Description';
import CreditContextContainer from './components/Description/components/Credit/context';
import Slider from './components/Slider';
import type { IContentPage } from './types';

import { BlockTitle, BlockWrapper } from '~/src/features/equipment/styled';
import * as ST from './styled';

const sMobile = '(max-width: 320px)';

const ContentPage = ({ item, category, productId, products }: IContentPage): JSX.Element => {
    const { isAuth, services } = useSelector((state: TState) => ({
        isAuth: state.auth.isAuth,
        services: state.auth.clientProducts?.services || [],
    }));

    const isSMobile = useMatchMedia(sMobile);

    const { image = '', images = [], bodyFull, category: itemCategory, props, labels, title, urls } = item;

    const imagesList = useMemo(() => {
        const list = [image, ...images];
        return list.length > 0 ? list : undefined;
    }, [image, images]);

    const router = useRouter();

    const isDecoder = useMemo(() => !!(router.pathname.indexOf('domru-tv') > -1), [router]);

    const isEngineerDeliveryAvailable: boolean = useMemo(() => {
        if (isDecoder) {
            return services.some(service => service.productId === EProductsId.tv);
        }
        return services.some(service => service.productId === EProductsId.internet || service.productId === EProductsId.tv);
    }, [isDecoder, services]);

    const baseRoute = router.pathname.replace('/[itemId]', '').replace('/_site/[city]', '');

    return (
        <>
            <ST.StyledWideContainer>
                <ST.ComponentWrapper isAuth={isAuth}>
                    {itemCategory && <ST.ProductCategory data-test='content-page-category'>{itemCategory.title}</ST.ProductCategory>}
                    {title && <ST.ProductTitle data-test='content-page-title'>{title}</ST.ProductTitle>}
                    <ST.ComponentGrid>
                        <ST.SliderWrapper>
                            <Slider
                                data-test='content-page-slider'
                                images={imagesList}
                                title={title}
                                labels={labels}
                            />
                        </ST.SliderWrapper>
                        <ST.DescriptionWrapper>
                            <CreditContextContainer>
                                <Description
                                    data-test='content-page-description'
                                    item={item}
                                    orderHandler={getOrderHandler(category, productId)}
                                    isEngineerDeliveryAvailable={isEngineerDeliveryAvailable}
                                />
                            </CreditContextContainer>
                        </ST.DescriptionWrapper>
                        <ST.CharacteristicsWrapper>
                            <Characteristics
                                data-test='content-page-characteristics'
                                specifications={props}
                                urls={urls}
                                description={bodyFull}
                            />
                        </ST.CharacteristicsWrapper>
                    </ST.ComponentGrid>

                    <ST.RoutersContainer isBlock>
                        <SegmentedBannerGrid alias={'lk_internet_equipment'} margin='64px 0'/>
                    </ST.RoutersContainer>

                    {!!products?.length && (
                        <ST.RoutersContainer>
                            <BlockTitle data-test='content-page-products-title' withCarousel={true}>{'Оборудование для любых задач'}</BlockTitle>
                            <BlockWrapper isSmallCards={isSMobile}>
                                <Routers
                                    data-test='content-page-products-list'
                                    routers={products}
                                    cellSpacing={8}
                                    baseRoute={baseRoute}
                                />
                            </BlockWrapper>
                        </ST.RoutersContainer>
                    )}
                </ST.ComponentWrapper>
            </ST.StyledWideContainer>
        </>
    );
};

export default ContentPage;
